/**
 * src/components/consent/CookieBanner.tsx
 *
 * KVKK/GDPR çerez onay banner'ı. Karar verilmemişse (decided=false) görünür.
 *
 * KVKK kuralları (kvkk-compliance.md):
 *   - "Tümünü Reddet" = "Tümünü Kabul Et" ile EŞİT görünürlük (ikisi de dolu buton)
 *   - 3 kategori, granüler toggle (Zorunlu her zaman açık, Analitik, Pazarlama)
 *   - Modal DEĞİL — sayfayı bloklamaz, focus-trap yok (role="region")
 *   - Çerez Politikası linki banner içinde
 *
 * i18n (next-intl 'consent'), dark/light (next-themes), marka renkleri
 * (brand + accent paleti). Client Component.
 */

'use client'

import { useLocale, useTranslations } from 'next-intl'
import { Cookie } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useConsent } from './ConsentProvider'

/**
 * Çerez Politikası locale-aware path (routeSegments ile uyumlu).
 * Slug i18n messages'tan gelir; yeni dil eklenirken sadece
 * messages/{locale}.json'a components.cookie_banner.policy_slug eklenir.
 */
function cookiePolicyHref(locale: string, slug: string): string {
  return `/${locale}/${slug}`
}

/**
 * CategoryToggle — tek çerez kategorisi satırı (başlık + açıklama + switch).
 * `locked` ise (Zorunlu kategori) switch devre dışı + "her zaman açık".
 */
function CategoryToggle({
  title,
  description,
  checked,
  locked,
  alwaysOnLabel,
  onChange,
}: {
  title: string
  description: string
  checked: boolean
  locked?: boolean
  alwaysOnLabel: string
  onChange?: (next: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/40">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900 dark:text-white">{title}</p>
        <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
          {description}
        </p>
      </div>
      {locked ? (
        <span className="flex-none text-xs font-medium text-brand-600 dark:text-brand-400">
          {alwaysOnLabel}
        </span>
      ) : (
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={title}
          onClick={() => onChange?.(!checked)}
          className={`relative inline-flex h-6 w-11 flex-none items-center rounded-full transition-colors ${
            checked ? 'bg-brand-600' : 'bg-slate-300 dark:bg-slate-600'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              checked ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </button>
      )}
    </div>
  )
}

/**
 * CookieBanner — onay banner'ı. decided=true ise null.
 */
export function CookieBanner() {
  const t = useTranslations('consent')
  const tc = useTranslations('components.cookie_banner')
  const locale = useLocale()
  const { decided, save } = useConsent()

  const [mounted, setMounted] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [analytics, setAnalytics] = useState(true)
  const [marketing, setMarketing] = useState(false)

  // Giriş animasyonu (translate-y) için bir tick sonra mount işaretle.
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(id)
  }, [])

  if (decided) return null

  const policyHref = cookiePolicyHref(locale, tc('policy_slug'))

  return (
    <div
      role="region"
      aria-label={t('aria_label')}
      className={`fixed inset-x-0 bottom-0 z-50 px-3 pb-3 transition-transform duration-300 motion-reduce:transition-none sm:px-4 sm:pb-4 print:hidden ${
        mounted ? 'translate-y-0' : 'translate-y-full'
      }`}
    >
      <div className="mx-auto max-w-4xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
        {/* Üst marka şeridi */}
        <div className="h-1 w-full bg-gradient-to-r from-brand-600 via-brand-500 to-accent-500" />

        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <span className="hidden flex-none rounded-xl bg-brand-50 p-2.5 text-brand-600 dark:bg-brand-950/50 dark:text-brand-400 sm:inline-flex">
              <Cookie size={22} aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-bold text-slate-900 dark:text-white">
                {t('title')}
              </h2>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                {t('description')}{' '}
                <a
                  href={policyHref}
                  className="font-medium text-brand-700 underline underline-offset-2 hover:text-brand-600 dark:text-brand-300"
                >
                  {t('policy_link')}
                </a>
              </p>
            </div>
          </div>

          {/* Kategori detayları (özelleştir açıkken) */}
          {showDetails && (
            <div className="mt-4 space-y-2.5">
              <CategoryToggle
                title={t('cat_necessary_title')}
                description={t('cat_necessary_desc')}
                checked
                locked
                alwaysOnLabel={t('always_on')}
              />
              <CategoryToggle
                title={t('cat_analytics_title')}
                description={t('cat_analytics_desc')}
                checked={analytics}
                alwaysOnLabel={t('always_on')}
                onChange={setAnalytics}
              />
              <CategoryToggle
                title={t('cat_marketing_title')}
                description={t('cat_marketing_desc')}
                checked={marketing}
                alwaysOnLabel={t('always_on')}
                onChange={setMarketing}
              />
            </div>
          )}

          {/* Butonlar — Reddet/Kabul EŞİT görünürlük (ikisi de dolu buton). */}
          <div className="mt-5 flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-end">
            {showDetails ? (
              <button
                type="button"
                onClick={() => save({ analytics, marketing }, 'preferences')}
                className="order-1 rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 sm:order-3"
              >
                {t('save')}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowDetails(true)}
                className="order-3 rounded-lg px-5 py-2.5 text-sm font-semibold text-slate-600 underline-offset-2 transition-colors hover:text-slate-900 hover:underline dark:text-slate-400 dark:hover:text-white sm:order-1 sm:mr-auto"
              >
                {t('customize')}
              </button>
            )}
            <button
              type="button"
              onClick={() => save({ analytics: false, marketing: false })}
              className="order-2 rounded-lg bg-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
            >
              {t('reject_all')}
            </button>
            <button
              type="button"
              onClick={() => save({ analytics: true, marketing: true })}
              className="order-1 rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 sm:order-2"
            >
              {t('accept_all')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
